import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { autospedService } from "@/services/autospedService";
import type { DispatchCreatePayload, EmpresaListParams, FileListParams } from "@/types/autosped";

const keys = {
  empresas: (wsId: number, params?: EmpresaListParams) =>
    ["autosped", wsId, "empresas", params ?? {}] as const,
  files: (wsId: number, clientId: number, params?: FileListParams) =>
    ["autosped", wsId, "files", clientId, params ?? {}] as const,
};

export function useAutospedEmpresas(workspaceId: number | null, params?: EmpresaListParams) {
  return useQuery({
    queryKey: keys.empresas(workspaceId ?? 0, params),
    queryFn: () => autospedService.listEmpresas(workspaceId as number, params),
    enabled: workspaceId != null,
    // Sem refetchInterval (decisão: SSE pro ao vivo). O global desliga
    // refetchOnWindowFocus — religa aqui como fallback pro stream morrer.
    refetchOnWindowFocus: true,
  });
}

export function useAutospedFiles(
  workspaceId: number | null,
  clientId: number | null,
  params?: FileListParams,
) {
  return useQuery({
    queryKey: keys.files(workspaceId ?? 0, clientId ?? 0, params),
    queryFn: () => autospedService.listFiles(workspaceId as number, clientId as number, params),
    enabled: workspaceId != null && clientId != null,
    refetchOnWindowFocus: true,
  });
}

export function useCreateDispatch(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DispatchCreatePayload) =>
      autospedService.createDispatch(workspaceId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["autosped", workspaceId] }),
  });
}

export function useReexecutar(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dispatchId, forcar }: { dispatchId: number; forcar?: boolean }) =>
      autospedService.reexecutar(workspaceId, dispatchId, forcar ?? false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["autosped", workspaceId] }),
  });
}
